package com.zi.support.shared

/**
 * The web template builder's view of a report template (one HTML document filled in with Mustache).
 * The visual editor edits only the body; the template's own CSS is kept verbatim, so rules the editor
 * can't represent (@page, print colours) survive. Mustache section tags ({{#rows}} … {{/rows}}) are
 * swapped for HTML comments while editing, because a browser moves bare text out of a table.
 */
object TemplateHtml {
    class Parts(val title: String, val headExtra: String, val css: String, val body: String)

    /** CSS the builder's elements (tb-* classes) need; added to a template once it uses one. */
    const val BLOCK_CSS_MARKER = "/* template builder */"
    val BLOCK_CSS = """
$BLOCK_CSS_MARKER
.tb-h1 { margin: 0 0 6pt; font-size: 22pt; line-height: 1.1; color: #111827; }
.tb-h2 { margin: 12pt 0 6pt; font-size: 14pt; color: #111827; }
.tb-h3 { margin: 10pt 0 4pt; font-size: 11pt; color: #344054; }
.tb-label { color: #667085; font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
.tb-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 12pt; padding-bottom: 6pt; margin-bottom: 10pt; border-bottom: 2pt solid #12324d; }
.tb-field { display: flex; align-items: flex-end; gap: 6pt; margin: 6pt 0; }
.tb-field > span { font-weight: 700; white-space: nowrap; }
.tb-field > i { flex: 1; min-height: 14pt; border-bottom: .75pt solid #667085; font-style: normal; }
.tb-value { margin: 4pt 0; }
.tb-value > span { display: block; }
.tb-box { min-height: 60pt; margin: 6pt 0; padding: 6pt 8pt; border: .75pt solid #98a2b3; border-radius: 4pt; }
.tb-check { display: flex; align-items: center; gap: 6pt; margin: 4pt 0; }
.tb-check > b { display: inline-grid; place-items: center; width: 10pt; height: 10pt; flex: 0 0 10pt; border: .75pt solid #344054; border-radius: 2pt; font-size: 8pt; }
.tb-cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12pt; }
.tb-cols3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12pt; }
.tb-section { margin: 8pt 0; padding: 8pt 10pt; border: .75pt solid #e4e7ec; border-radius: 5pt; }
.tb-table { width: 100%; margin: 6pt 0; border-collapse: collapse; }
.tb-table th, .tb-table td { padding: 4pt 6pt; border: .75pt solid #d0d5dd; text-align: left; vertical-align: top; }
.tb-table th { background: #f2f4f7; font-size: 8pt; }
.tb-divider { margin: 10pt 0; border: 0; border-top: .75pt solid #d0d5dd; }
.tb-spacer { height: 18pt; }
.tb-pagebreak { break-after: page; height: 0; }
.tb-sign { display: inline-block; min-width: 160pt; margin-top: 24pt; padding-top: 3pt; border-top: .75pt solid #344054; color: #667085; font-size: 8pt; }
.tb-badge { display: inline-block; padding: 2pt 8pt; border-radius: 99pt; background: #eef2f6; font-size: 8pt; font-weight: 700; }
.tb-image { max-width: 100%; height: auto; }
""".trimStart()

    private val STYLE = Regex("<style[^>]*>([\\s\\S]*?)</style>", RegexOption.IGNORE_CASE)
    private val TITLE = Regex("<title[^>]*>([\\s\\S]*?)</title>", RegexOption.IGNORE_CASE)
    private val HEAD = Regex("<head[^>]*>([\\s\\S]*?)</head>", RegexOption.IGNORE_CASE)
    private val BODY = Regex("<body[^>]*>([\\s\\S]*?)</body>", RegexOption.IGNORE_CASE)
    private val SECTION = Regex("\\{\\{\\s*([#^/])\\s*([^}]+?)\\s*\\}\\}")
    private val PROTECTED = Regex("<!--\\s*(\\{\\{[#^/][^}]*\\}\\})\\s*-->")
    private val TAG = Regex("<[^>]*>")
    // GrapesJS drops comments at the very start of the page, so the protected body starts with this.
    private const val START = "<span data-tb-start></span>"
    private val START_TAG = Regex("<span data-tb-start[^>]*>\\s*</span>\\s*")

    fun split(html: String): Parts {
        val head = HEAD.find(html)?.groupValues?.get(1).orEmpty()
        val headExtra = head.replace(STYLE, "").replace(TITLE, "").replace(Regex("<meta\\s+charset[^>]*>", RegexOption.IGNORE_CASE), "").trim()
        val body = BODY.find(html)?.groupValues?.get(1)
            ?: html.replace(HEAD, "").replace(Regex("</?(html|!doctype)[^>]*>", RegexOption.IGNORE_CASE), "")
        return Parts(TITLE.find(head)?.groupValues?.get(1)?.trim().orEmpty(), headExtra,
            STYLE.findAll(html).joinToString("\n") { it.groupValues[1].trim('\n') }, body.trim())
    }

    /** The whole document again; [css] gets the builder's element CSS when the body uses a tb-* class. */
    fun join(parts: Parts, extraCss: String = ""): String {
        val needsBlocks = "class=\"tb-" in parts.body || " tb-" in parts.body
        val css = listOf(parts.css.trim(), if (needsBlocks && BLOCK_CSS_MARKER !in parts.css) BLOCK_CSS.trim() else "", extraCss.trim())
            .filter { it.isNotBlank() }.joinToString("\n\n")
        return buildString {
            append("<!doctype html>\n<html lang=\"en\">\n<head>\n<meta charset=\"utf-8\">\n")
            append("<title>").append(parts.title).append("</title>\n")
            if (parts.headExtra.isNotBlank()) append(parts.headExtra).append('\n')
            append("<style>\n").append(css).append("\n</style>\n</head>\n<body>\n").append(parts.body.trim()).append("\n</body>\n</html>\n")
        }
    }

    /**
     * Ready for the visual editor: {{#x}}, {{^x}} and {{/x}} between tags become <!--{{#x}}--> so they stay in place,
     * every style attribute and any class holding data move to data-tb-style / data-tb-class (GrapesJS drops or rewrites
     * inline styles and class names, but keeps other attributes as they are). The class keeps its plain part, so the
     * canvas still looks right; the builder paints data-tb-style onto the canvas itself.
     */
    fun protect(body: String): String {
        val out = StringBuilder(START)
        var last = 0
        TAG.findAll(body).forEach { tag ->
            out.append(commentSections(body.substring(last, tag.range.first)))
            out.append(stashAttributes(tag.value))
            last = tag.range.last + 1
        }
        out.append(commentSections(body.substring(last)))
        return out.toString()
    }

    fun unprotect(body: String): String = body.replace(START_TAG, "").replace(TAG) { restoreAttributes(it.value) }.replace(PROTECTED) { it.groupValues[1] }

    /** Why a body can't be edited visually (data the editor would lose), or null when it can. */
    fun visualBlocker(body: String): String? =
        if (TAG.findAll(protect(body)).any { !it.value.startsWith("<!--") && SECTION.containsMatchIn(it.value.replace(STASHED, "")) })
            "This template has {{#…}} sections inside an attribute the builder can't keep, so it can only be edited as code."
        else null

    private val DATA_ATTRIBUTE = Regex("\\s(class\\s*=\\s*\"(?=[^\"]*\\{\\{)|style\\s*=\\s*\")([^\"]*)\"")
    private val STASHED = Regex("\\sdata-tb-(class|style)=\"[^\"]*\"")
    private val SECTION_BLOCK = Regex("\\{\\{\\s*[#^]\\s*([^}]+?)\\s*\\}\\}[\\s\\S]*?\\{\\{\\s*/\\s*\\1\\s*\\}\\}")

    private fun commentSections(text: String) = text.replace(SECTION) { "<!--{{${it.groupValues[1]}${it.groupValues[2]}}}-->" }

    private fun stashAttributes(tag: String) = tag.replace(DATA_ATTRIBUTE) { match ->
        val name = match.groupValues[1].substringBefore('=').trim()
        val value = match.groupValues[2]
        val plain = value.replace(SECTION_BLOCK, "").replace(Regex("\\{\\{[^}]*\\}\\}\\}?"), "").trim().replace(Regex("\\s+"), " ")
        (if (name == "class" && plain.isNotEmpty()) " class=\"$plain\"" else "") + " data-tb-$name=\"$value\""
    }

    /** The stashed class/style back in place of whatever the editor wrote for them. */
    private fun restoreAttributes(tag: String): String {
        val stashed = STASHED.findAll(tag).associate { it.groupValues[1] to it.value.substringAfter('"').dropLast(1) }
        if (stashed.isEmpty()) return tag
        var out = tag.replace(STASHED, "")
        stashed.keys.forEach { name -> out = out.replace(Regex("\\s$name\\s*=\\s*\"[^\"]*\""), "") }
        val name = Regex("^<[^\\s>/]+").find(out)!!.value
        return name + stashed.entries.joinToString("") { (key, value) -> " $key=\"$value\"" } + out.removePrefix(name)
    }

    /** Sets the @page size, adding an @page rule when the CSS has none. */
    fun withPageSize(css: String, format: String, orientation: String): String {
        val size = "size: $format $orientation;"
        val page = Regex("@page\\s*\\{[^}]*\\}").find(css)
            ?: return "@page { $size margin: 16mm; }\n$css"
        val rule = page.value
        val updated = if (Regex("size\\s*:[^;}]*;?").containsMatchIn(rule)) rule.replace(Regex("size\\s*:[^;}]*;?"), size)
            else rule.replaceFirst("{", "{ $size")
        return css.replaceRange(page.range, updated)
    }

    /** Canvas width in CSS px for the page, so the editor shows the page at its printed width. */
    fun pageWidthPx(format: String, orientation: String): Int {
        val (short, long) = when (format) { "A3" -> 1123 to 1587; "Letter" -> 816 to 1056; else -> 794 to 1123 }
        return if (orientation == "landscape") long else short
    }

}
